/**
 * FAQ — Grid Cards
 * Dedicated copy (same convention as component-platform-features.js).
 * Hooks are prefixed data-faqgc-* to avoid the theme's existing "faq" section.
 *
 * The cards are never rebuilt. Ranking uses CSS `order`, hiding uses an
 * `.is-hidden` class, and moves between layouts are animated with FLIP
 * (measure, apply, play the difference) so search, filters and "load more"
 * all glide instead of jumping.
 *
 * Answers open either in a pop-up (<dialog>, default) or inside the card
 * ("expand"), chosen by the section's "How answers open" setting.
 */
(function () {
  'use strict';

  if (window.__faqgcBooted) return;
  window.__faqgcBooted = true;

  var EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  var REDUCE = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-faqgc-root]').forEach(init);
  }

  initAll(document);

  // Theme editor: re-init a section that was just added / re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-faqgc-root]').forEach(function (root) {
      if (root.__faqgc) root.__faqgc.destroy();
    });
  });

  /* ---------- text helpers ---------- */

  function textWithBreaks(el) {
    var out = '';
    el.childNodes.forEach(function (node) {
      if (node.nodeType === 3) out += node.textContent;
      else if (node.nodeName === 'BR') out += '\n';
      else out += node.textContent;
    });
    return out;
  }

  function escapeHtml(str) {
    return str.replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Escape first, then mark, then restore line breaks.
  function toHtml(text, query) {
    var escaped = escapeHtml(text);
    var words = query
      ? query.split(/\s+/).filter(Boolean).map(function (w) { return escapeRegExp(escapeHtml(w)); })
      : [];
    if (words.length) {
      escaped = escaped.replace(new RegExp('(' + words.join('|') + ')', 'ig'), '<mark>$1</mark>');
    }
    return escaped.replace(/\n/g, '<br>');
  }

  function plural(n) {
    return n === 1 ? 'question' : 'questions';
  }

  /* ---------- section ---------- */

  function init(root) {
    if (root.__faqgc) return;
    var grid = root.querySelector('[data-faqgc-grid]');
    if (!grid) return;

    var input = root.querySelector('.faq-grid-cards__search-input');
    var clearBtn = root.querySelector('.faq-grid-cards__search-clear');
    var pills = Array.prototype.slice.call(root.querySelectorAll('[data-faqgc-pill]'));
    var selectEl = root.querySelector('[data-faqgc-select]');
    var selectWrap = root.querySelector('[data-faqgc-select-wrap]');
    var emptyEl = root.querySelector('[data-faqgc-empty]');
    var resetBtn = root.querySelector('[data-faqgc-reset]');
    var metaEl = root.querySelector('[data-faqgc-meta]');
    var moreWrap = root.querySelector('[data-faqgc-more-wrap]');
    var moreBtn = root.querySelector('[data-faqgc-more]');
    var progressEl = root.querySelector('[data-faqgc-progress]');
    var modal = root.querySelector('[data-faqgc-modal]');

    var initial = parseInt(root.dataset.initial, 10) || 9;
    var step = parseInt(root.dataset.step, 10) || 9;
    var designMode = root.dataset.designMode === 'true';

    var reveal = root.dataset.reveal === 'expand' ? 'expand' : 'modal';
    if (reveal === 'modal' && !(modal && typeof modal.showModal === 'function')) reveal = 'expand';
    root.classList.toggle('is-modal', reveal === 'modal');
    root.classList.toggle('is-expand', reveal === 'expand');

    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-faqgc-card]')).map(function (el, i) {
      var qEl = el.querySelector('[data-faqgc-question-text]');
      var aEl = el.querySelector('[data-faqgc-answer-text]');
      var wrap = el.querySelector('[data-faqgc-answer-wrap]');
      var toggle = el.querySelector('[data-faqgc-toggle]');
      var qText = qEl ? textWithBreaks(qEl) : '';
      var aText = aEl ? textWithBreaks(aEl) : '';
      var label = el.dataset.categoryLabel || '';
      if (toggle && reveal === 'expand') {
        toggle.removeAttribute('aria-haspopup');
        toggle.setAttribute('aria-expanded', 'false');
        if (wrap && wrap.id) toggle.setAttribute('aria-controls', wrap.id);
      }
      return {
        el: el,
        index: i,
        handle: el.dataset.categoryHandle || '',
        label: label,
        color: el.dataset.catColor || '',
        qEl: qEl,
        aEl: aEl,
        wrap: wrap,
        toggle: toggle,
        qText: qText,
        aText: aText,
        q: qText.toLowerCase(),
        a: aText.toLowerCase(),
        c: label.toLowerCase(),
        order: i,
        shown: !el.classList.contains('is-hidden'),
        marked: false,
        anim: null
      };
    });

    // Random order on every page load (Fisher-Yates over each card's base position).
    if (root.dataset.shuffle === 'true' && !designMode) {
      var pool = cards.map(function (c) { return c.index; });
      for (var k = pool.length - 1; k > 0; k--) {
        var j = Math.floor(Math.random() * (k + 1));
        var tmp = pool[k]; pool[k] = pool[j]; pool[j] = tmp;
      }
      cards.forEach(function (c, n) { c.order = pool[n]; });
    }

    var total = cards.length;
    var state = { query: '', category: '', limit: initial };
    var matched = cards.slice();
    var visible = [];

    /* ----- search scoring ----- */

    function score(card, query, words) {
      var hay = card.q + ' ' + card.c + ' ' + card.a;
      for (var i = 0; i < words.length; i++) {
        if (hay.indexOf(words[i]) === -1) return 0;
      }
      var s = 0;
      if (card.q === query) s += 100;
      else if (card.q.indexOf(query) === 0) s += 40;
      else if (card.q.indexOf(query) !== -1) s += 30;
      if (card.c.indexOf(query) !== -1) s += 18;
      if (card.a.indexOf(query) !== -1) s += 12;
      words.forEach(function (w) {
        if (card.q.indexOf(w) !== -1) s += 6;
        if (card.a.indexOf(w) !== -1) s += 2;
      });
      return s || 1;
    }

    function compute() {
      var query = state.query.trim().toLowerCase();
      var words = query.split(/\s+/).filter(Boolean);
      var scored = [];
      cards.forEach(function (c) {
        if (state.category && c.handle !== state.category) return;
        var s = query ? score(c, query, words) : 1;
        if (query && s <= 0) return;
        scored.push({ c: c, s: s });
      });
      scored.sort(function (x, y) { return y.s - x.s || x.c.order - y.c.order; });
      matched = scored.map(function (x) { return x.c; });
    }

    /* ----- render (FLIP) ----- */

    function render(animate) {
      compute();
      var query = state.query.trim();
      var hasQuery = query.length > 0;
      var hasFilter = hasQuery || !!state.category;

      visible = matched.slice(0, state.limit);
      var visSet = new Set(visible);
      var rank = new Map();
      visible.forEach(function (c, i) { rank.set(c, i); });

      var first = new Map();
      var doAnim = animate && !REDUCE;
      if (doAnim) {
        cards.forEach(function (c) {
          if (c.shown) first.set(c, c.el.getBoundingClientRect());
        });
      }
      cards.forEach(function (c) {
        if (c.anim) { c.anim.cancel(); c.anim = null; }
      });

      var entering = [];
      cards.forEach(function (c) {
        var show = visSet.has(c);
        c.el.classList.toggle('is-hidden', !show);
        c.el.style.order = show ? String(rank.get(c)) : '9999';
        if (show && !c.shown) entering.push(c);
        c.shown = show;

        if (hasQuery) {
          if (c.qEl) c.qEl.innerHTML = toHtml(c.qText, query);
          if (c.aEl) c.aEl.innerHTML = toHtml(c.aText, query);
          c.marked = true;
        } else if (c.marked) {
          if (c.qEl) c.qEl.innerHTML = toHtml(c.qText, '');
          if (c.aEl) c.aEl.innerHTML = toHtml(c.aText, '');
          c.marked = false;
        }
      });

      if (doAnim && typeof Element.prototype.animate === 'function') {
        visible.forEach(function (c) {
          var f = first.get(c);
          if (!f) return;
          var l = c.el.getBoundingClientRect();
          var dx = f.left - l.left;
          var dy = f.top - l.top;
          if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
          c.anim = c.el.animate(
            [{ transform: 'translate(' + dx + 'px,' + dy + 'px)' }, { transform: 'none' }],
            { duration: 440, easing: EASE }
          );
        });
        entering.forEach(function (c, i) {
          c.anim = c.el.animate(
            [
              { opacity: 0, transform: 'translateY(16px) scale(0.98)' },
              { opacity: 1, transform: 'none' }
            ],
            { duration: 460, delay: Math.min(i, 8) * 45, easing: EASE, fill: 'backwards' }
          );
        });
      }

      // Count line
      if (metaEl) {
        if (!hasFilter) {
          metaEl.innerHTML = '<strong>' + total + '</strong> ' + plural(total) + ' available';
        } else if (matched.length === 0) {
          metaEl.textContent = 'No matching questions';
        } else {
          metaEl.innerHTML =
            '<strong>' + matched.length + '</strong> of ' + total + ' ' + plural(total) + ' match';
        }
      }

      if (emptyEl) emptyEl.hidden = matched.length !== 0;
      if (resetBtn) resetBtn.hidden = !hasFilter;

      if (moreWrap) {
        var hasMore = matched.length > visible.length;
        moreWrap.hidden = !hasMore;
        if (progressEl) progressEl.textContent = 'Showing ' + visible.length + ' of ' + matched.length;
      }
    }

    function resetLimit() {
      state.limit = initial;
    }

    /* ----- search / filters / load more ----- */

    if (input) {
      input.addEventListener('input', function () {
        state.query = input.value;
        if (clearBtn) clearBtn.hidden = input.value.length === 0;
        resetLimit();
        render(true);
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && input.value) {
          e.preventDefault();
          clearSearch(false);
        }
      });
    }

    function clearSearch(focus) {
      if (input) input.value = '';
      state.query = '';
      if (clearBtn) clearBtn.hidden = true;
      resetLimit();
      render(true);
      if (focus && input) input.focus();
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () { clearSearch(true); });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        state.category = '';
        pills.forEach(function (p) {
          var on = (p.dataset.category || '') === '';
          p.classList.toggle('is-active', on);
          p.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        if (selectEl) {
          selectEl.value = '';
          if (selectWrap) selectWrap.classList.remove('is-active');
        }
        clearSearch(true);
      });
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        state.category = pill.dataset.category || '';
        pills.forEach(function (p) {
          var on = p === pill;
          p.classList.toggle('is-active', on);
          p.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        resetLimit();
        render(true);
      });
    });

    if (selectEl) {
      selectEl.addEventListener('change', function () {
        state.category = selectEl.value || '';
        if (selectWrap) selectWrap.classList.toggle('is-active', !!state.category);
        resetLimit();
        render(true);
      });
    }

    if (moreBtn) {
      moreBtn.addEventListener('click', function () {
        var before = visible.length;
        state.limit += step;
        render(true);
        var next = matched[before];
        if (next && next.toggle) next.toggle.focus();
      });
    }

    /* ----- opening answers ----- */

    cards.forEach(function (c) {
      c.el.addEventListener('click', function (e) {
        if (e.target.closest('[data-faqgc-answer-wrap]')) return;
        if (reveal === 'modal') openModal(c);
        else toggleCard(c);
      });
    });

    function toggleCard(c) {
      var open = c.el.classList.toggle('is-open');
      if (c.toggle) c.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    /* ----- pop-up ----- */

    var current = null;
    var opener = null;
    var locked = false;
    var lockPrev = { overflow: '', paddingRight: '' };

    var mChip, mTitle, mBody, mCount, mPrev, mNext, mClose;
    if (modal) {
      mChip = modal.querySelector('[data-faqgc-modal-chip]');
      mTitle = modal.querySelector('[data-faqgc-modal-title]');
      mBody = modal.querySelector('[data-faqgc-modal-body]');
      mCount = modal.querySelector('[data-faqgc-modal-count]');
      mPrev = modal.querySelector('[data-faqgc-modal-prev]');
      mNext = modal.querySelector('[data-faqgc-modal-next]');
      mClose = modal.querySelector('[data-faqgc-modal-close]');
    }

    function lock() {
      if (locked) return;
      locked = true;
      var h = document.documentElement;
      lockPrev.overflow = h.style.overflow;
      lockPrev.paddingRight = h.style.paddingRight;
      var gutter = window.innerWidth - h.clientWidth;
      if (gutter > 0) h.style.paddingRight = gutter + 'px';
      h.style.overflow = 'hidden';
    }

    function unlock() {
      if (!locked) return;
      locked = false;
      var h = document.documentElement;
      h.style.overflow = lockPrev.overflow;
      h.style.paddingRight = lockPrev.paddingRight;
    }

    function fillModal(c, dir) {
      current = c;
      var query = state.query.trim();
      mTitle.textContent = c.qText;
      mBody.innerHTML = toHtml(c.aText, query);
      mBody.scrollTop = 0;
      if (c.label && root.dataset.chips !== 'false') {
        mChip.hidden = false;
        mChip.textContent = c.label;
        mChip.style.backgroundColor = c.color || '';
      } else {
        mChip.hidden = true;
      }
      var idx = matched.indexOf(c);
      mCount.textContent = idx === -1 ? '' : idx + 1 + ' of ' + matched.length;
      mPrev.disabled = idx <= 0;
      mNext.disabled = idx === -1 || idx >= matched.length - 1;
      opener = c.toggle;

      if (dir && !REDUCE && typeof mBody.animate === 'function') {
        var shift = dir * 14;
        [mTitle, mBody].forEach(function (el) {
          el.animate(
            [{ opacity: 0, transform: 'translateX(' + shift + 'px)' }, { opacity: 1, transform: 'none' }],
            { duration: 300, easing: EASE }
          );
        });
      }
    }

    function openModal(c) {
      modal.classList.remove('is-closing');
      fillModal(c, 0);
      if (!modal.open) modal.showModal();
      lock();
    }

    function finishClose() {
      if (!modal.classList.contains('is-closing') && !modal.open) return;
      modal.classList.remove('is-closing');
      if (modal.open) modal.close();
      unlock();
      if (opener && opener.offsetParent !== null) opener.focus();
    }

    function closeModal() {
      if (!modal.open || modal.classList.contains('is-closing')) return;
      if (REDUCE) {
        finishClose();
        return;
      }
      modal.classList.add('is-closing');
      setTimeout(finishClose, 190);
    }

    function go(dir) {
      if (!current) return;
      var idx = matched.indexOf(current) + dir;
      if (idx < 0 || idx >= matched.length) return;
      var target = matched[idx];
      // Make sure the card we land on exists in the grid (so focus can return to it).
      if (!target.shown) {
        state.limit = Math.max(state.limit, idx + 1);
        render(false);
      }
      fillModal(target, dir);
    }

    if (modal && reveal === 'modal') {
      mClose.addEventListener('click', closeModal);
      mPrev.addEventListener('click', function () { go(-1); });
      mNext.addEventListener('click', function () { go(1); });

      modal.addEventListener('cancel', function (e) {
        e.preventDefault();
        closeModal();
      });
      modal.addEventListener('close', unlock);

      // Click on the backdrop (the dialog element itself, outside its box).
      modal.addEventListener('click', function (e) {
        if (e.target !== modal) return;
        var r = modal.getBoundingClientRect();
        var inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
        if (!inside) closeModal();
      });

      modal.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      });
    }

    /* ----- first paint + entrance ----- */

    render(false);
    root.classList.add('is-ready');

    var canAnimate = !REDUCE && !designMode && typeof Element.prototype.animate === 'function';
    if (canAnimate && 'IntersectionObserver' in window) {
      grid.style.opacity = '0';
      var io = new IntersectionObserver(
        function (entries) {
          if (!entries.some(function (en) { return en.isIntersecting; })) return;
          io.disconnect();
          grid.style.opacity = '';
          visible.forEach(function (c, i) {
            c.anim = c.el.animate(
              [
                { opacity: 0, transform: 'translateY(18px)' },
                { opacity: 1, transform: 'none' }
              ],
              { duration: 520, delay: Math.min(i, 9) * 55, easing: EASE, fill: 'backwards' }
            );
          });
        },
        { threshold: 0.08 }
      );
      io.observe(grid);
    }

    root.__faqgc = {
      destroy: function () {
        if (modal && modal.open) modal.close();
        unlock();
      }
    };
  }
})();